import moment from 'moment';
import type { ServerTimeType } from '../types';
import { dailyToDay } from '../constants/date';
import { getTimeSpaceArray } from '@/pages/opinion/utils';
import { formatTimeToServer } from '@op/utils/time';

export const isSameTime =
  (dateType: ServerTimeType) => (startTime: string, endTime: string) => {
    return moment(startTime).isSame(endTime, dailyToDay[dateType]);
  };

export const batchCompareTime = (
  newTimeList: Array<string>,
  oldTimeList: Array<string>,
) => {
  if (newTimeList.length !== oldTimeList.length) {
    return false;
  }

  const compareTime = isSameTime('hourly');
  return newTimeList.every((newTime, index) => {
    const oldTime = oldTimeList[index];
    return compareTime(newTime, oldTime);
  });
};

export const dateMap: { [key in ServerTimeType]?: any } = {
  daily: {
    formate: 'YYYY-MM-DD',
    moment_type: 'day',
    before_text: 'Yesterday',
    suffix: 'Days',
    current: 'Today',
  },
  weekly: {
    formate: 'YYYY-MM-DD',
    moment_type: 'week',
    before_text: 'Last Week',
    suffix: 'Weeks',
    current: 'This Week',
  },
  monthly: {
    formate: 'YYYY-MM',
    moment_type: 'month',
    before_text: 'Last Month',
    suffix: 'Months',
    current: 'This Month',
  },
};

export const dealwithDate = (
  startTime: string,
  endTime: string,
  dateType: ServerTimeType = 'daily',
) => {
  const { formate, moment_type } = dateMap[dateType] || dateMap.daily;
  const currentDate = moment().subtract(1, 'day').format(formate); // 当前时间
  const beforeDate = moment().subtract(1, moment_type).format(formate); // 前一周期时间

  const sameTimeFun = isSameTime(dateType);

  if (sameTimeFun(currentDate, endTime) && !sameTimeFun(startTime, endTime)) {
    const times = getTimeSpaceArray(
      startTime,
      endTime,
      moment_type,
      formate,
      true,
    );
    return times.length;
  } else if (
    sameTimeFun(beforeDate, endTime) &&
    sameTimeFun(startTime, endTime)
  ) {
    return -1;
  }
  return 0;
};

/**
 * 验证时间字符串是否为 YYYY-MM-DD HH:mm:ss 格式
 */
const isValidDateTimeFormat = (timeStr: string): boolean => {
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timeStr);
};

/**
 * 格式化时间为标准格式 YYYY-MM-DD HH:mm:ss
 * @param start_time - 开始时间
 * @param end_time - 结束时间
 * @param date_type - 日期类型，如果是 hourly 则保持原有时分秒
 * @returns 格式化后的时间对象 { start_time, end_time }
 */
export const formatDateTimeRange = (
  start_time: string,
  end_time: string,
  date_type?: ServerTimeType,
): { start_time: string; end_time: string } => {
  const formatStartTime = (time: string): string => {
    if (isValidDateTimeFormat(time)) {
      return time;
    }
    const m = moment(time);
    if (!m.isValid()) {
      return time;
    }
    // 如果是 hourly，保持原有时分秒
    if (date_type === 'hourly') {
      return m.format('YYYY-MM-DD HH:mm:ss');
    }
    // 如果是 monthly，转为当月第一天的 00:00:00
    if (date_type === 'monthly') {
      return m.startOf('month').format('YYYY-MM-DD 00:00:00');
    }
    // 其他类型，时分秒设为 00:00:00
    return m.format('YYYY-MM-DD 00:00:00');
  };

  const formatEndTime = (time: string): string => {
    if (isValidDateTimeFormat(time)) {
      return time;
    }
    const m = moment(time);
    if (!m.isValid()) {
      return time;
    }
    // 如果是 hourly，保持原有时分秒
    if (date_type === 'hourly') {
      return m.format('YYYY-MM-DD HH:mm:ss');
    }
    // 如果是 monthly，转为当月最后一天的 23:59:59
    if (date_type === 'monthly') {
      return m.endOf('month').format('YYYY-MM-DD 23:59:59');
    }
    // 其他类型，时分秒设为 23:59:59
    return m.format('YYYY-MM-DD 23:59:59');
  };

  return {
    start_time: formatStartTime(start_time),
    end_time: formatEndTime(end_time),
  };
};

export const resumeDateTime = (
  start_time: string,
  end_time: string,
  dateType: ServerTimeType = 'daily',
  time_value: number,
) => {
  function backParamsInfo(times: [string, string]) {
    return {
      start_time: times[0],
      end_time: times[1],
    };
  }

  const { formate, moment_type } = dateMap[dateType] || {
    formate: 'YYYY-MM-DD HH:mm:ss',
  };
  const current = moment().format(formate);
  if (typeof time_value === 'string' && time_value === 'Year to Date') {
    const start_time = moment(`${moment().year()}-01-01`).format(formate);
    const end_time =
      (moment(`${moment().year()}-01-01`).format(formate), current);
    return backParamsInfo(formatTimeToServer([start_time, end_time], 'daily'));
  }

  if (time_value === 0 || time_value === -1) {
    let startDate = null;
    let endDate = null;
    if (moment_type === 'week') {
      startDate = moment()
        .week(moment().week() + time_value)
        .startOf('week')
        .add(0, 'days')
        .format(formate);
      endDate = moment()
        .week(moment().week() + time_value)
        .endOf('week')
        .add(0, 'days')
        .format(formate);
    } else if (moment_type === 'month') {
      startDate = moment()
        .month(moment().month() + time_value)
        .startOf('month')
        .format(formate);
      endDate = moment()
        .month(moment().month() + time_value)
        .endOf('month')
        .format(formate);
    } else {
      endDate = startDate = moment().format(formate);

      if (time_value === -1) {
        endDate = startDate = moment(startDate)
          .subtract(1, 'day')
          .format(formate);
      }
    }
    return backParamsInfo(formatTimeToServer([startDate, endDate], 'daily'));
  }

  if (time_value > 0) {
    return backParamsInfo(
      formatTimeToServer(
        [
          moment()
            .subtract(time_value - 1, moment_type)
            .format(formate),
          current,
        ],
        'daily',
      ),
    );
  }

  return { start_time, end_time };
};
